'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import {
  AppliedDiscountCode,
  calculateAvailableDiscountCodes,
  DiscountCode,
  sortDiscountCodes,
  useQueryDiscountCodes
} from 'modules/benefitCodes'
import { extractTokenFromClientSession } from 'modules/customer'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { isNotNil, isNotNilOrEmpty } from 'shared/utils/ramda'

import useCartStore from '../../../store/cartStore'
import { Cart } from '../../../types'

export type UseRebateCodes = {
  open: boolean
  promoCode: string
  setPromoCode: (code: string) => void
  resetTrigger: number
  activeCodes: AppliedDiscountCode[]
  providedRebateCodes: DiscountCode[] | null
  hasCode: boolean
  isCodeCardsVisible: boolean
  rebateCodesLabel: string
  handleOpen: () => void
  handleClose: () => void
  handleAddNewCode: (code: string) => void
  handleRemoveCode: (code: string) => () => void
}
const useDiscountCodes = (cart: Cart | null): UseRebateCodes => {
  const [open, setOpen] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [providedRebateCodes, setProvidedRebateCodes] = useState<
    DiscountCode[] | null
  >(null)
  const [numberOfAvailableCodes, setNumberOfAvailableCodes] = useState(0)
  const [resetTrigger, setResetTrigger] = useState(0)
  const [activeCodes, setActiveCodes] = useState<AppliedDiscountCode[] | []>([])

  const { addDiscountCodeToCart, removeDiscountCodeFromCart } = useCartStore()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const { data: rebateCodes } = useQueryDiscountCodes(token || '')

  useEffect(() => {
    if (cart?.discountCodes) {
      setActiveCodes(cart.discountCodes)
    } else {
      setActiveCodes([])
    }
  }, [cart?.discountCodes])

  useEffect(() => {
    if (rebateCodes) {
      const sortedRebateCodes = sortDiscountCodes(rebateCodes, cart)
      const newNumberOfAvailableCodes =
        calculateAvailableDiscountCodes(sortedRebateCodes)

      setProvidedRebateCodes(sortedRebateCodes)
      setNumberOfAvailableCodes(newNumberOfAvailableCodes)
    }
  }, [rebateCodes, cart])

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  const handleInputReset = useCallback(() => {
    setResetTrigger((prev: number) => prev + 1)
    setPromoCode('')
  }, [])

  const handleAddNewCode = useCallback(
    (code: string) => {
      addDiscountCodeToCart(code, session)
        .then(() => {
          toast.success('Dodano kod rabatowy: ' + code)
          syneriseSendFormData({
            formData: {
              code
            },
            event: SyneriseFormEvent.REBATE_CODE_ADD
          })
          handleInputReset()
          handleClose()
        })
        .catch(err => {
          toast.error(getApiErrors(err))
          handleInputReset()
        })
    },
    [addDiscountCodeToCart, handleInputReset, handleClose, session]
  )

  const handleRemoveCode = useCallback(
    (codeId: string) => () => {
      removeDiscountCodeFromCart(codeId, session)
        .then(handleInputReset)
        .catch(err => {
          toast.error(getApiErrors(err))
          handleInputReset()
        })
    },
    [removeDiscountCodeFromCart, handleInputReset, session]
  )

  const hasCode = isNotNilOrEmpty(activeCodes)
  const isCodeCardsVisible =
    isNotNil(providedRebateCodes) && session.status === 'authenticated'
  const rebateCodesLabel =
    numberOfAvailableCodes > 0 && session.status === 'authenticated'
      ? `Sprawdź dostępne kody rabatowe: ${numberOfAvailableCodes}`
      : 'Wpisz kod rabatowy'

  return {
    open,
    promoCode,
    setPromoCode,
    resetTrigger,
    activeCodes,
    providedRebateCodes,
    hasCode,
    isCodeCardsVisible,
    rebateCodesLabel,
    handleOpen,
    handleClose,
    handleAddNewCode,
    handleRemoveCode
  }
}

export default useDiscountCodes
